/**
 * Review Step Component
 *
 * Third and final step in the create table sheet wizard.
 * Displays a summary of the table configuration before creation.
 *
 * Features:
 * - Table name and file summary
 * - Field list preview
 * - Folder location display
 * - Create button with loading state
 *
 * @module features/workspace/components/CreateTableSheet/steps/ReviewStep
 */

import {
  ArrowLeft,
  Check,
  FileText,
  Folder,
  Hash,
  Loader2,
  Table,
} from 'lucide-react';
import { Button } from '@loader/ui';

import type { ReviewStepProps } from '../types';

import { formatFileSize } from '@/lib/fileUtils';


// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface SummaryItemProps {
  icon: React.ElementType;
  label: string;
  value: string;
  secondary?: string;
}

/**
 * Single-line summary item matching ConfigureStep field layout
 */
function SummaryItem({ icon: Icon, label, value, secondary }: SummaryItemProps) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="font-medium text-sm truncate">{value}</span>
      {secondary && (
        <span className="text-xs text-muted-foreground">{secondary}</span>
      )}
    </div>
  );
}


// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Review step for the create table sheet
 *
 * Displays a summary of all configuration and allows final creation.
 */
export function ReviewStep({
  tableName,
  fileName,
  fileSize,
  fieldCount,
  folderName,
  isCreating,
  onCreate,
  onBack,
}: ReviewStepProps) {
  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Review & Create</h3>
      </div>

      {/* Summary */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="rounded-lg border bg-card divide-y">
          <SummaryItem
            icon={Table}
            label="Name"
            value={tableName}
          />
          <SummaryItem
            icon={FileText}
            label="File"
            value={fileName}
            secondary={formatFileSize(fileSize)}
          />
          {folderName && (
            <SummaryItem
              icon={Folder}
              label="Location"
              value={folderName}
            />
          )}
          <SummaryItem
            icon={Hash}
            label="Fields"
            value={`${fieldCount}`}
          />
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="flex gap-2 mt-auto">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={isCreating}
          className="flex-1"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button
          onClick={onCreate}
          disabled={isCreating}
          className="flex-1"
        >
          {isCreating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <Check className="mr-2 h-4 w-4" />
              Create Table
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
